// src/pages/admin/users/CreateUser.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardHeader, CardBody, Typography } from "@material-tailwind/react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../../../firebaseConfig";

// تطبيق Firebase ثانوي لإنشاء الحساب بدون التأثير على جلسة الأدمن
import { getApp, initializeApp, deleteApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";

import { ArrowRightIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

const BRAND = {
  ink: "#2F2F2F",
  graphite: "#878787",
  silver: "#B8B8B8",
  mist: "#D0D0D0",
  paper: "#EFEFEF",
  white: "#FCFCFC",
};

export default function CreateUser() {
  const [me, setMe] = useState(null);
  const [role, setRole] = useState(Number(localStorage.getItem("role") || "2"));
  const isAdmin = role === 1;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [storeName, setStoreName] = useState("");
  const [phone, setPhone] = useState("");
  const [newRole, setNewRole] = useState("2");

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const nav = useNavigate();

  const ui = useMemo(() => {
    return {
      pageBg: `radial-gradient(1200px 520px at 90% -30%, ${BRAND.paper}, ${BRAND.white} 55%)`,
      cardBorder: `1px solid ${BRAND.paper}`,
      cardShadow: "0 18px 50px rgba(0,0,0,0.06), 0 6px 18px rgba(0,0,0,0.04)",
      headerBg: `linear-gradient(135deg, ${BRAND.ink}, ${BRAND.graphite})`,
      btnBg: `linear-gradient(135deg, ${BRAND.ink}, ${BRAND.graphite})`,
      ring: "0 0 0 4px rgba(135,135,135,0.18)",
    };
  }, []);

  const inputCls =
    "w-full rounded-2xl border px-3 py-3 outline-none transition font-tajawal bg-white";
  const btnPrimary =
    "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-extrabold transition disabled:opacity-60";
  const btnGhost =
    "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-extrabold transition";

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setMe(u || null);
      const r = Number(localStorage.getItem("role") || "2");
      setRole(r);
    });
    return () => unsub();
  }, []);

  async function submit() {
    if (!isAdmin) {
      setErr("هذه الصفحة للمشرف فقط.");
      return;
    }
    setErr("");
    setOk("");

    if (!email || !password || !storeName) {
      setErr("املأ البريد، كلمة المرور، واسم المتجر.");
      return;
    }

    setLoading(true);
    let secondaryApp;

    try {
      const baseApp = getApp();
      secondaryApp = initializeApp(baseApp.options, "admin-secondary");
      const secondaryAuth = getAuth(secondaryApp);

      const cred = await createUserWithEmailAndPassword(
        secondaryAuth,
        email.trim(),
        password
      );
      const newUser = cred.user;

      if (storeName) await updateProfile(newUser, { displayName: storeName });

      await setDoc(
        doc(db, "users", newUser.uid),
        {
          role: Number(newRole),
          email: email.trim(),
          displayName: storeName,
          storeName: storeName,
          phone: phone || "",
          createdAt: serverTimestamp(),
          createdBy: me?.uid || null,
        },
        { merge: true }
      );

      setOk(`تم إنشاء الحساب بنجاح. UID: ${newUser.uid}`);
      setEmail("");
      setPassword("");
      setStoreName("");
      setPhone("");

      await signOut(secondaryAuth);
      await deleteApp(secondaryApp);
      secondaryApp = undefined;
    } catch (e) {
      console.error(e);
      const code = e?.code || "";
      if (code === "auth/email-already-in-use") setErr("هذا البريد مستخدم مسبقًا.");
      else if (code === "auth/invalid-email") setErr("بريد إلكتروني غير صالح.");
      else if (code === "auth/weak-password") setErr("كلمة المرور ضعيفة. جرّب كلمة أقوى.");
      else setErr(e?.message || "تعذّر إنشاء الحساب.");

      try {
        if (secondaryApp) {
          const sauth = getAuth(secondaryApp);
          await signOut(sauth).catch(() => {});
          await deleteApp(secondaryApp).catch(() => {});
        }
      } catch {}
    } finally {
      setLoading(false);
    }
  }

  function goBack() {
    if (window.history.length > 1) nav(-1);
    else nav("/");
  }

  if (!isAdmin) {
    return (
      <div dir="rtl" className="min-h-screen p-6" style={{ background: ui.pageBg }}>
        <div
          className="mx-auto max-w-xl rounded-3xl px-5 py-4"
          style={{ background: BRAND.paper, border: `1px solid ${BRAND.mist}`, color: BRAND.ink }}
        >
          <div className="font-tajawal font-extrabold">هذه الصفحة للمشرف فقط.</div>
        </div>
      </div>
    );
  }

  return (
    <div dir="rtl" className="p-4 min-h-screen font-tajawal" style={{ background: ui.pageBg }}>
      <Card
        className="mx-auto max-w-2xl rounded-3xl overflow-hidden bg-white"
        style={{ border: ui.cardBorder, boxShadow: ui.cardShadow }}
      >
        <CardHeader floated={false} shadow={false} className="p-0">
          <div className="flex items-center justify-between px-6 py-6" style={{ background: ui.headerBg }}>
            <button
              onClick={goBack}
              className="group inline-flex items-center gap-2 rounded-2xl px-3 py-2 transition"
              style={{
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.18)",
                color: BRAND.white,
                fontFamily: "Tajawal, system-ui, sans-serif",
              }}
              title="رجوع"
            >
              <ArrowRightIcon className="h-5 w-5" />
              <span className="text-sm font-extrabold">رجوع</span>
            </button>

            <div className="text-center flex-1">
              <Typography variant="h4" className="text-white font-extrabold font-tajawal">
                إنشاء حساب مستخدم
              </Typography>
              <div className="mt-1 text-xs" style={{ color: "rgba(255,255,255,0.78)" }}>
                تصميم رمادي بالكامل — مثل الصورة
              </div>
            </div>

            <span className="w-[92px]" />
          </div>
        </CardHeader>

        <CardBody className="space-y-4 p-6">
          {(err || ok) && (
            <div className="space-y-2">
              {err && (
                <div
                  className="rounded-2xl px-4 py-3 font-tajawal"
                  style={{ background: BRAND.paper, border: `1px solid ${BRAND.mist}`, color: BRAND.ink }}
                >
                  {err}
                </div>
              )}
              {ok && (
                <div
                  className="rounded-2xl px-4 py-3 font-tajawal"
                  style={{ background: BRAND.white, border: `1px solid ${BRAND.mist}`, color: BRAND.ink }}
                >
                  {ok}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
              البريد الإلكتروني
            </label>
            <input
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ borderColor: BRAND.mist, color: BRAND.ink }}
              onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
              onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
              كلمة المرور
            </label>
            <input
              className={inputCls}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ borderColor: BRAND.mist, color: BRAND.ink }}
              onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
              onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
              اسم المتجر / المستخدم
            </label>
            <input
              className={inputCls}
              value={storeName}
              onChange={(e) => setStoreName(e.target.value)}
              style={{ borderColor: BRAND.mist, color: BRAND.ink }}
              onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
              onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
              الهاتف (اختياري)
            </label>
            <input
              className={inputCls}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={{ borderColor: BRAND.mist, color: BRAND.ink }}
              onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
              onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
              الدور
            </label>
            <select
              className={inputCls}
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              style={{ borderColor: BRAND.mist, color: BRAND.ink }}
              onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
              onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
            >
              <option value="1">أدمن</option>
              <option value="2">موظّف</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button
              className={btnPrimary + " w-full"}
              onClick={submit}
              disabled={loading}
              style={{
                background: ui.btnBg,
                color: BRAND.white,
                boxShadow: "0 12px 28px rgba(0,0,0,0.10)",
                fontFamily: "Tajawal, system-ui, sans-serif",
              }}
            >
              <UserPlusIcon className="h-5 w-5" />
              {loading ? "جارٍ الإنشاء..." : "إنشاء الحساب"}
            </button>

            <button
              className={btnGhost}
              type="button"
              onClick={goBack}
              style={{
                background: BRAND.white,
                border: `1px solid ${BRAND.mist}`,
                color: BRAND.ink,
                fontFamily: "Tajawal, system-ui, sans-serif",
              }}
            >
              رجوع
            </button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
